import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, resource } from '@angular/core';
import { lastValueFrom, map, throwError } from 'rxjs';
import { Option } from '../../models/common.models';
import {
  CreateChildWarehouseRequest,
  RmaChannelConfig,
  RmaChannelConfigResponse,
  RmaConfigurationResponse,
  SubInventoryMappingResponse,
  UpdateRmaConfigurationRequest,
  WarehouseChannelAllocation,
  WarehouseChannelAllocationResponse,
  WarehouseOption,
  WarehouseResponse,
} from '../../models/warehouse.models';

function parseRmaTypes(acceptedRmaTypes: string): { isRegular: boolean; isCreditOnly: boolean } {
  const types = acceptedRmaTypes ?? '';
  return {
    isRegular: types.toLowerCase().includes('regular'),
    isCreditOnly: types.toLowerCase().includes('credit only'),
  };
}
import { CommonService } from '../common/common.service';
import { RegionService } from '../region/region.service';
import { mapWarehouseResponseToModel } from './warehouse.mapper';

@Injectable({
  providedIn: 'root',
})
export class WarehouseService {
  private readonly http = inject(HttpClient);
  private readonly common = inject(CommonService);
  private readonly regions = inject(RegionService);

  private readonly warehouses$ = (regionId: number) =>
    this.http
      .get<WarehouseResponse[]>('Warehouse', {
        params: new HttpParams().set('region_id', String(regionId)),
      })
      .pipe(
        map((response: WarehouseResponse[]) =>
          response.map((response) => mapWarehouseResponseToModel(response)),
        ),
      );

  warehouses = resource({
    params: () => this.regions.selectedRegionId(),
    loader: ({ params }) => lastValueFrom(this.warehouses$(params)),
    defaultValue: [],
  });

  warehouseOptions = computed<WarehouseOption[]>(() =>
    this.warehouses.value().map((warehouse) => {
      return {
        label: warehouse.name,
        value: warehouse.code,
        dccCode: warehouse.dccCode,
        wmsCode: warehouse.wmsCode,
      };
    }),
  );

  uniqueDccCodes = computed<string[]>(
    () =>
      this.common.getUniquePropertyValues(
        this.warehouseOptions(),
        'dccCode',
      ) as string[],
  );

  dccCodeOptions = computed<Option[]>(() =>
    this.uniqueDccCodes().map((code) => {
      return { label: code, value: code };
    }),
  );

  uniqueWmsCodes = computed<string[]>(
    () =>
      this.common.getUniquePropertyValues(
        this.warehouseOptions(),
        'wmsCode',
      ) as string[],
  );

  wmsCodeOptions = computed<Option[]>(() =>
    this.uniqueWmsCodes().map((code) => {
      return { label: code, value: code };
    }),
  );

  findWarehouse(code: string) {
    return this.warehouses.value().find((w) => w.code === code);
  }

  getWarehouse = (dcc_code: string, entity_code: string, wms_code: string) => {
    const params = new HttpParams()
      .set('dcc_code', dcc_code)
      .set('entity_code', entity_code)
      .set('wms_code', wms_code);

    return this.http.get('Warehouse', { params });
  };

  downloadWarehouses(dcc_code: string, entity_code: string, wms_code: string) {
    const params = new HttpParams()
      .set('dcc_code', dcc_code)
      .set('entity_code', entity_code)
      .set('wms_code', wms_code);

    return this.http.get('Warehouse/download', {
      params,
      responseType: 'blob',
      observe: 'response',
    });
  }

  getWarehouseById = (code: string) => {
    return this.http
      .get<WarehouseResponse>('Warehouse/' + code)
      .pipe(
        map((response: WarehouseResponse) =>
          mapWarehouseResponseToModel(response),
        ),
      );
  };

  createChildWarehouse(parentWarehouseCode: string, payload: CreateChildWarehouseRequest) {
    return this.http.post<WarehouseResponse>(
      `Warehouse/${parentWarehouseCode}/children/`,
      payload,
    ).pipe(
      map((response) => mapWarehouseResponseToModel(response)),
    );
  }

  getSubInventoryMappings(parentWarehouseCode: string) {
    return this.http.get<SubInventoryMappingResponse[] | null>(
      `Warehouse/${parentWarehouseCode}/sub-inventory-mappings`,
    ).pipe(
      map((response) => response || []),
    );
  }

  getChildSubInventory(childWarehouseCode: string) {
    return this.http.get<string[] | null>(
      `Warehouse/${childWarehouseCode}/child-sub-inventory`,
    ).pipe(
      map((response) => response || []),
    );
  }

  getChannelAllocations(warehouseCode: string) {
    return this.http
      .get<WarehouseChannelAllocationResponse[] | null>(
        `Warehouse/${warehouseCode}/channel-allocation`,
      )
      .pipe(
        map((response) =>
          (response || []).map((allocation) => ({
            channelCode: allocation.channel_code,
            inventoryAllocation: allocation.inventory_allocation,
          })),
        ),
      );
  }

  updateChannelAllocations(
    warehouseCode: string,
    allocations: WarehouseChannelAllocation[],
  ) {
    const totalAllocation = allocations.reduce(
      (sum, allocation) => sum + (allocation.inventoryAllocation || 0),
      0,
    );

    if (totalAllocation > 100) {
      return throwError(() => new Error('Total allocation cannot be greater than 100%'));
    }
    else{
           const payload = allocations.map((allocation) => ({
      channel_code: allocation.channelCode,
      inventory_allocation: allocation.inventoryAllocation,
    }));

    
    return this.http
      .put<WarehouseChannelAllocationResponse[]>(
        `Warehouse/${warehouseCode}/channel-inventory-allocation`,
        payload,
      )
      .pipe(
        map((response) =>
          response.map((allocation) => ({
            channelCode: allocation.channel_code,
            inventoryAllocation: allocation.inventory_allocation,
          })),
        ),
      );
    }
  }

  getRmaChannelConfigs(warehouseCode: string) {
    return this.http
      .get<RmaChannelConfigResponse[]>(
        `Warehouse/${warehouseCode}/channel-allocation`,
      )
      .pipe(
        map((response) =>
          (response || []).map((item): RmaChannelConfig => ({
            channelCode: item.channel_code,
            acceptRma: item.accept_rma ?? false,
            autoApproveRma: item.auto_approve_rma ?? false,
            ...parseRmaTypes(item.accepted_rma_types),
            inventoryAllocation: item.inventory_allocation ?? 0,
          })),
        ),
      );
  }

  getRmaConfiguration(warehouseCode: string, channelCode: string) {
    return this.http.get<RmaConfigurationResponse>(
      `Warehouse/${warehouseCode}/${channelCode}/rma-configuration`,
    );
  }

  updateRmaConfiguration(
    warehouseCode: string,
    channelCode: string,
    payload: UpdateRmaConfigurationRequest,
  ) {
    return this.http.put(
      `Warehouse/${warehouseCode}/${channelCode}/rma-configuration`,
      payload,
      { responseType: 'text' },
    );
  }
}